/**
 * MobileBottomNav Component
 * Bottom tab navigation bar for mobile devices (visible below md breakpoint)
 */

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Plus, BarChart3, Lightbulb, User } from 'lucide-react';
import { useAnalysisDialogStore } from '@/stores/analysisDialogStore';
import { cn } from '@/lib/utils';

interface NavItem {
  to?: string;
  action?: string;
  labelKey: string;
  icon: React.ReactNode;
  highlight?: boolean;
}

const navItems: NavItem[] = [
  { to: '/dashboard', labelKey: 'sidebar.dashboard', icon: <Home className="w-5 h-5" /> },
  { to: '/analysis-results', labelKey: 'sidebar.myAnalyses', icon: <BarChart3 className="w-5 h-5" /> },
  { action: 'openAnalysisDialog', labelKey: 'sidebar.newAnalysis', icon: <Plus className="w-6 h-6" strokeWidth={2.5} />, highlight: true },
  { to: '/analyst', labelKey: 'sidebar.analyst', icon: <Lightbulb className="w-5 h-5" /> },
  { to: '/profile', labelKey: 'sidebar.profile', icon: <User className="w-5 h-5" /> },
];

export function MobileBottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const openAnalysisDialog = useAnalysisDialogStore((s) => s.open);

  const nonResultsPaths = ['/analysis/multichannel-kampagne'];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    if (path === '/analysis-results') {
      return (location.pathname.startsWith('/analysis-results') ||
        location.pathname.startsWith('/analysis/')) &&
        !nonResultsPaths.some((p) => location.pathname.startsWith(p));
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden mobile-bottom-nav"
      role="navigation"
      aria-label={t('sidebar.mainNavigation')}
    >
      <div className="flex items-center justify-around px-2 pt-2 pb-[env(safe-area-inset-bottom)]">
        {navItems.map((item) => {
          const key = item.to ?? item.action!;

          // Highlighted center button (Neue Analyse)
          if (item.highlight && item.action) {
            return (
              <button
                key={key}
                onClick={
                  item.action === 'openAnalysisDialog' ? openAnalysisDialog :
                  undefined
                }
                data-track="mobile_nav_new_analysis"
                className="flex flex-col items-center gap-0.5 min-w-0 cursor-pointer -mt-5"
              >
                <span className="flex items-center justify-center w-12 h-12 rounded-full gradient-bg text-white shadow-lg transition-all duration-200 active:scale-95">
                  {item.icon}
                </span>
                <span className="text-[10px] font-medium truncate max-w-[64px] gradient-text">
                  {t(item.labelKey)}
                </span>
              </button>
            );
          }

          if (item.action) {
            return (
              <button
                key={key}
                onClick={
                  item.action === 'openAnalysisDialog' ? openAnalysisDialog :
                  undefined
                }
                data-track={`mobile_nav_${item.action}`}
                className={cn(
                  'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-0 cursor-pointer',
                  'text-gray-400'
                )}
              >
                {item.icon}
                <span className="text-[10px] font-medium truncate max-w-[64px]">
                  {t(item.labelKey)}
                </span>
              </button>
            );
          }
          const active = isActive(item.to!);
          return (
            <Link
              key={key}
              to={item.to!}
              data-track={`mobile_nav_${item.to!.replace(/^\//, '').replace(/[-/]/g, '_') || 'dashboard'}`}
              className={cn(
                'flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-0',
                active
                  ? 'text-[var(--color-primary-start)]'
                  : 'text-gray-400'
              )}
            >
              {item.icon}
              <span className="text-[10px] font-medium truncate max-w-[64px]">
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
